// Menu (MOD-03, config-tier) types.
//
// Bifurkasyon kararı (yeni ekran): BE'nin ürettiği `components['schemas']`
// tipleri WIRE contract olarak kullanılır; UI için alanları garanti (non-optional)
// bir FE tipine map'lenir (generated tipte her alan optional).
//
// Menü çok-seviyeli öğe ağacı taşır. Konum/link-tipi/hedef değerleri BE
// sabitlerini birebir aynalar (MenuLocations / MenuLinkTypes). Config-tier
// iki-durum publish: Draft ↔ Published.

import type { components } from '@/types/api';

/** BE wire DTO'ları (generated) — envelope içinde gelen ham şekiller. */
export type MenuDtoWire = components['schemas']['MenuDto'];
export type MenuListItemDtoWire = components['schemas']['MenuListItemDto'];
export type MenuItemDtoWire = components['schemas']['MenuItemDto'];
export type CreateMenuRequest = components['schemas']['CreateMenuRequest'];
export type UpdateMenuRequest = components['schemas']['UpdateMenuRequest'];
export type CreateMenuItemRequest = components['schemas']['CreateMenuItemRequest'];
export type UpdateMenuItemRequest = components['schemas']['UpdateMenuItemRequest'];
export type MenuReorderItemWire = components['schemas']['MenuReorderItem'];

/** Menü konumu — BE `MenuLocations` sabitiyle birebir. */
export const MENU_LOCATIONS = ['header', 'footer', 'sidebar', 'mobile'] as const;
export type MenuLocation = (typeof MENU_LOCATIONS)[number];

/** Öğe link tipi — BE `MenuLinkTypes` sabitiyle birebir (url veya içerik-referansı). */
export const MENU_LINK_TYPES = ['url', 'article', 'page', 'campaign'] as const;
export type MenuLinkType = (typeof MENU_LINK_TYPES)[number];

/** İçerik-referanslı link tipleri (contentSlug taşır, url taşımaz). */
export const CONTENT_LINK_TYPES: readonly MenuLinkType[] = ['article', 'page', 'campaign'];

/** Anchor hedefi (MenuItem.Target). */
export const MENU_TARGETS = ['_self', '_blank'] as const;
export type MenuTarget = (typeof MENU_TARGETS)[number];

/** Config-tier iki-durum yayın durumu (BaseStatus'un string alt kümesi). */
export type ConfigStatus = 'Draft' | 'Published';

/** BE'den gelen ham status string'ini iki-durum config statüsüne daraltır. */
export function normalizeConfigStatus(status: string | null | undefined): ConfigStatus {
  return status === 'Published' ? 'Published' : 'Draft';
}

/** UI-facing menü liste satırı. */
export interface MenuListItem {
  id: string;
  name: string;
  location: string;
  languageId: string;
  groupId: string;
  status: ConfigStatus;
  itemCount: number;
  updatedAt: string;
}

/** UI-facing menü öğesi (çok-seviye ağaç düğümü). */
export interface MenuItemNode {
  id: string;
  menuId: string;
  parentId: string | null;
  label: string;
  linkType: MenuLinkType;
  url: string | null;
  contentSlug: string | null;
  target: string | null;
  order: number;
  isActive: boolean;
  children: MenuItemNode[];
}

/** UI-facing menü (detay) — öğe ağacıyla birlikte. */
export interface Menu {
  id: string;
  name: string;
  location: string;
  languageId: string;
  groupId: string;
  status: ConfigStatus;
  variantLanguageCodes: string[];
  items: MenuItemNode[];
}

export interface MenuListQuery {
  location?: string;
  languageId?: string;
}

export interface MenuCreateInput {
  name: string;
  location: string;
  /** İçerik i18n (satır-başına-dil). Boşsa BE varsayılan dili kullanır. */
  languageId?: string | null;
  groupId?: string | null;
}

export interface MenuUpdateInput {
  name: string;
  location: string;
}

/** Öğe oluşturma girdisi (parentId + order taşır). */
export interface MenuItemCreateInput {
  label: string;
  linkType: MenuLinkType;
  parentId?: string | null;
  url?: string | null;
  contentSlug?: string | null;
  target?: string | null;
  order?: number | null;
  isActive: boolean;
}

/** Öğe güncelleme girdisi — parentId/order YOK (yeniden konum/sıra reorder ile). */
export interface MenuItemUpdateInput {
  label: string;
  linkType: MenuLinkType;
  url?: string | null;
  contentSlug?: string | null;
  target?: string | null;
  isActive: boolean;
}

/** Reorder düğümü — sıra + üst öğe (re-parent destekli). */
export interface MenuReorderNode {
  id: string;
  order: number;
  parentId: string | null;
}
